import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Toronto = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const getAbout = () => {
    const torontoLink = isChinese
      ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
      : 'https://liv.rent/blog/neighbourhoods/where-to-live-in-toronto/'

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.toronto:About Toronto Paragraph 1">
            With almost half of the population born outside of Canada, Toronto
            is one of the most multicultural cities in the world. From high-rise
            condominium towers dominating the Downtown Core to uniquely
            Torontonian bay-and-gable housing in Old Toronto and pre-war
            single-family houses in the suburbs, Toronto,{' '}
            <LinkText href="/rental-listings/ontario">Ontario</LinkText> offers
            a vast array of housing and rental options suitable for everyone in
            all stages of life.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.toronto:About Toronto Paragraph 2">
            Known for its gorgeous natural spaces and extensive public transit
            system, Toronto is consistently ranked as one of the most livable
            cities. Serving as Canada’s banking and financial capital, the city
            of Toronto continues to grow with the{' '}
            <LinkText href={torontoLink}>Greater Toronto Area (GTA)</LinkText>{' '}
            being home to over 6 million people.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink="https://liv.rent/blog/renters/top-tips-for-renting-in-toronto/"
        rentingTipsLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%a6%82%e4%bd%95%e5%9c%a8%e5%a4%9a%e4%bc%a6%e5%a4%9a%e7%a7%9f%e5%85%ac%e5%af%93%ef%bc%9f%e8%bf%9910%e7%82%b9%e6%9c%80%e9%87%8d%e8%a6%81%ef%bc%81/"
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$2,200 - $2,800',
          bed2: '$3,000 - $4,000',
          bed3: '$4,000 - $4,900'
        }}
        rentReportCity={t('cities:city_name_toronto')}
        rentReportLink="https://liv.rent/blog/rent-reports/toronto/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E5%A4%9A%E4%BC%A6%E5%A4%9A/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.toronto:Apartment Hunting in Toronto Paragraph 1">
            Since it’s Canada’s most populous city and consistently one of its
            most expensive, it will be hard to find{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/cheapest-places-to-rent-greater-toronto-area/">
              cheap rentals in Toronto
            </LinkText>{' '}
            - especially if you’re trying to find{' '}
            <LinkText href={links.under1000}>
              a place to rent for under $1000
            </LinkText>
            . However, to start your search, you can refer to this guide on the{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/cheapest-neighbourhoods-in-the-city-of-toronto/">
              cheapest neighbourhoods for rent in City of Toronto
            </LinkText>
            .
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.toronto:Apartments for rent Toronto Paragraph">
              Apartments are the most common form of housing you’ll find in
              Toronto. Renters should be prepared for higher prices for{' '}
              <LinkText href={links.apartment}>
                apartments for rent in Toronto
              </LinkText>
              , especially compared to other Canadian cities. Still, diligent
              renters should be able to find something within their budget in
              this sprawling metropolis.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.toronto:Houses for rent Toronto Paragraph">
              Though the city is very densely populated, there are quieter areas
              of Toronto surrounding the city’s downtown where renters may find{' '}
              <LinkText href={links.house}>houses for rent in Toronto</LinkText>
              . Both partial houses and entire houses for rent are common, with
              a mix of older and newer homes depending on which part of the city
              you’re looking in.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Townhouses for rent CITY Paragraph">
              Townhouses are a popular form of housing in {{ cityName }} that
              combines the best qualities of both apartments and whole houses.
              Typically less expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in {{ cityName }}
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.toronto:Basements for rent Toronto Paragraph">
              A popular alternative for students, first-time renters and anyone
              else seeking relatively inexpensive rental housing are basements
              for rent in Toronto. Since rent prices for apartments and houses
              can often get too expensive, many renters seek out basements for
              rent in Toronto as a cheaper alternative. Homeowners often rent
              out their basements as a way to offset their mortgage payments.
              This makes it easy for renters to find relatively inexpensive
              basements for rent in all areas of the city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.toronto:Condos for rent Toronto Paragraph">
              Particularly in more densely-populated areas like Downtown
              Toronto,{' '}
              <LinkText href={links.apartment}>
                condos for rent in Toronto
              </LinkText>{' '}
              are often the most common form of rental housing. These types of
              rental accommodation are perfect for those who like to be in the
              centre of the action, and don’t need too much space. If you are
              looking to rent in the city centre though, be prepared for
              significantly higher monthly rent prices.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            {t(
              'seo.city.toronto:Short term rentals Toronto intro paragraph',
              `As Canada's largest city, `
            )}
            <Trans i18nKey="seo:Short term rentals CITY Paragraph">
              {{ cityName }} has plenty of work opportunities and world-class
              academic institutions, making it a popular place for those renting
              on a short-term basis.{' '}
              <LinkText href={links.shortTerm}>
                Short-term rentals in {{ cityName }}
              </LinkText>{' '}
              are exceedingly popular, particularly during the summer months and
              at the beginning of school semesters. The added flexibility is an
              appealing prospect for many renters, and a key factor in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are right for you
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.toronto:Cheap rentals Toronto Paragraph">
              Toronto is famous across Canada for its high rent prices, but if
              you’re thoughtful in your search, it shouldn’t be too hard to find
              something within your budget. If you’re looking for{' '}
              <LinkText href="https://liv.rent/blog/renters/what-can-1800-in-rent-get-you-in-toronto/">
                cheap rentals in Toronto, ON
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in Toronto where
              rent is considerably cheaper, so exploring these options is a good
              idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    const uotOffCampus = isChinese
      ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%9a%e4%bc%a6%e5%a4%9a%e5%a4%a7%e5%ad%a63%e5%a4%a7%e6%a0%a1%e5%8c%ba%e7%a7%9f%e6%88%bf%e6%94%bb%e7%95%a5/'
      : 'https://liv.rent/blog/neighbourhoods/best-housing-off-campus-for-university-of-toronto-students/'

    const metroOffCampus = isChinese
      ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/the-best-off-campus-housing-at-toronto-metropolitan-university-cn/'
      : 'https://liv.rent/blog/neighbourhoods/off-campus-guide-toronto-metropolitan-university/'

    const yorkOffCampus = isChinese
      ? 'https://liv.rent/blog/zh/rental-resources-zh/the-best-off-campus-housing-for-york-university-students-cn/'
      : 'https://liv.rent/blog/neighbourhoods/best-off-campus-housing-york-university/'

    const familyHoods = isChinese
      ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/the-8-best-neighbourhoods-for-families-in-toronto-cn/'
      : 'https://liv.rent/blog/neighbourhoods/best-neighbourhoods-for-families-in-toronto/'

    const petHoods = isChinese
      ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/pet-friendly-neighbourhoods-toronto-cn/'
      : 'https://liv.rent/blog/neighbourhoods/top-pet-friendly-neighbourhoods-in-toronto/'

    return (
      <Paragraph>
        <Trans i18nKey="seo.city.toronto:Living in Toronto Paragraph">
          Toronto is a truly world-class city, with easy access to the very best
          Canada has to offer. For students, schools like The{' '}
          <LinkText href={uotOffCampus}>University of Toronto</LinkText>,{' '}
          <LinkText href={metroOffCampus}>
            Toronto Metropolitan University
          </LinkText>
          , and <LinkText href={yorkOffCampus}>York University</LinkText> each
          draw in tens of thousands each year spread across the city’s various
          neighbourhoods. For those with families looking to move to Toronto,
          there are a number of{' '}
          <LinkText href={familyHoods}>family-friendly neighbourhoods</LinkText>
          , as well as{' '}
          <LinkText href={petHoods}>
            neighbourhoods that are particularly appealing to pet owners
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getPopularHoods = () => {
    const hoodsLink = isChinese
      ? 'https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E5%AE%89%E5%A4%A7%E7%95%A5%E7%9C%81-%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/'
      : 'https://liv.rent/blog/neighbourhoods/where-to-live-in-toronto/'

    return (
      <Paragraph>
        <Trans i18nKey="seo.city.toronto:Popular Neighbourhoods Toronto Paragraph">
          Toronto is made up of a number of diverse, eclectic neighbourhoods
          spread across the city’s four administrative districts:
          Etobicoke-York, North York, Toronto, and East Scarborough. For more
          about Toronto’s individual neighbourhoods, you can consult this guide
          on <LinkText href={hoodsLink}>Toronto neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.toronto:Local Landmarks Toronto Paragraph">
          Known for its arts scene, sporting events, vibrant nightlife, and
          cuisines from all nationalities, Toronto has it all. Some of the most
          iconic landmarks include the CN Tower, Kensington Market, Eaton’s
          Centre, Yonge Street, Distillery District, Royal Ontario Museum,
          Rogers Centre, Ripley’s Aquarium of Canada, Toronto Islands,
          University of Toronto and Casa Loma.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Toronto
